import {GATE_TYPE, PANEL_TYPE, USER_JOURNEY_ACTION_TYPE} from "../../../Constants";
import {panelModule, PanelPlacement} from "../../PanelModule";
import {SessionActionThrottler} from "../../SessionActionThrottler";
import {walletModule} from "../../WalletModule";
import {merchantFunctionModule} from "../../merchant-state/MerchantFunctionModule";
import {evaluateTemplate} from "../../TemplateEvaluation";
import {userDataModule} from "../../UserDataModule";
import {uxFlowModule} from "../../UXFlowModule";
import {getMerchantUserJourney} from "../../PublicData";
import {type PanelPromptOptions} from "../../../PanelOptionsLogic";
import {merchantVariableModule} from "../../merchant-state/MerchantVariableModule";
import {markURLVisited} from "../../../utils/PageAccessHelpers.js";
import {base64Decode, toArray} from "../../../../stem-core/base/Utils.js";
import {editPanel} from "../../../PanelOptionsLogic";
import {isString} from "../../../../stem-core/base/Utils.js";
import {type Journey, type JourneyAction} from "./Condition";
import {type PanelElementOptions} from "../../../ui/elements/PanelElement";
import type {JourneyConditionEvaluator} from "./JourneyConditionEvaluator";

export type BlinkAction = string | JourneyAction;


export class JourneyActionRunner {
    journey: Journey;
    actions: JourneyAction[];
    throttler: SessionActionThrottler | null;
    // The evaluator that owns what this puts up, and takes it down once the journey's condition turns false. What an
    // unowned run (a blink action) puts up stays up, unthrottled.
    owner: JourneyConditionEvaluator | null;

    constructor(journey: Journey, owner: JourneyConditionEvaluator | null = null) {
        const {action, throttle} = journey.options;

        this.journey = journey;
        this.actions = toArray(action);
        this.owner = owner;

        if (owner && throttle) {
            let throttleOptions = evaluateTemplate(toArray(throttle));
            this.throttler = new SessionActionThrottler(journey.alias + "-throttle", throttleOptions);
        } else {
            this.throttler = null;
        }
    }

    // Returns the placements the actions put up, so the evaluator can take them down when the journey condition
    // later flips false (e.g. navigating to a non-gated page in an SPA). Returns null when the actions were declined
    // rather than run, so the caller knows this wasn't the journey's turn yet and it should keep trying, instead of
    // recording it as "already ran".
    runAction(): PanelPlacement[] | null {
        for (const action of this.actions) {
            if (action.type === USER_JOURNEY_ACTION_TYPE.createPanel && action.gateType === GATE_TYPE.popup) {
                // TODO: Re-visit these quirks of the journey popups. This is
                //  for compatibility with the old DonationsModule implementation.
                //  Maybe move this to PanelModule, or have explicit priority
                //  per-flow, etc. Or maybe just add these as conditions explicitly
                //  in the associated journeys.

                // The journey popups have the smallest priority, so if widget is
                // expanded / popup is displayed, we don't show it.
                if (walletModule.isExpanded() || walletModule.isPopupFlowActive()) {
                    return null;
                }
            }
        }

        if (!this.chargeThrottle()) {
            return null;
        }

        return this.runActions();
    }

    // After a login, while the journey's condition still holds: the actions run again, so templates that read the
    // login state catch up. A panel that comes out the same stays up, the buyer may be logging in through it.
    // Returns the placements up afterwards.
    rerun(placements: PanelPlacement[]): PanelPlacement[] {
        if (!this.chargeThrottle()) {
            return placements;
        }
        const rerunPlacements = this.runActions(placements);
        // The old ones come down first, since a gate restores the container style it found at mount
        for (const placement of placements) {
            if (!rerunPlacements.includes(placement)) {
                placement.remove();
            }
        }
        for (const placement of rerunPlacements) {
            if (!placements.includes(placement)) {
                panelModule.addPlacement(placement);
            }
        }
        return rerunPlacements;
    }

    // Counts a run against the journey's throttle, or with dryRun only asks. False when the throttle holds it back.
    chargeThrottle(dryRun = false): boolean {
        return this.throttler == null || this.throttler.execute(dryRun);
    }

    // For after a regate, which already put the panels back up.
    runNonPanelActions(): void {
        // A callback's createPanel key is a panel too
        for (const {createPanel, ...action} of this.actions) {
            if (action.type !== USER_JOURNEY_ACTION_TYPE.createPanel) {
                runJourneyAction(this, action);
            }
        }
    }

    runActions(previous?: PanelPlacement[]): PanelPlacement[] {
        const placements: PanelPlacement[] = [];
        for (const action of this.actions) {
            const placement = runJourneyAction(this, action, previous);
            if (placement) {
                placements.push(placement);
            }
        }
        return placements;
    }
}

// A popup is shown once, since walletModule owns its lifecycle. An inline panel becomes a placement: in the
// selector's first match, or with `every`, in every match, current and future. It's returned when the run is owned.
// On a rerun, `previous` holds what the run before put up: a popup isn't shown again, an equal placement is
// returned instead of a new one, and a new one is left for the rerun to add.
export function createJourneyPanel(action: Record<string, any>, journey?: Journey, runner?: JourneyActionRunner, previous?: PanelPlacement[]): PanelPlacement | undefined {
    // Reported like any merchant function's errors, since a bad action is merchant config
    try {
        const {every, lazy, selector, ...options} = action;
        if (journey) {
            options.journeyId = journey.id;
            options.journeyAlias = journey.alias;
        }
        if (options.gateType === GATE_TYPE.popup || (!every && !runner?.owner)) {
            // Only an owned run reruns, so this skips just the popups
            if (!previous) {
                panelModule.createPanel(selector, options);
            }
            return;
        }
        const placement = new PanelPlacement(selector, options as PanelElementOptions, {
            every: !!every,
            // Every match's iframes grow with the page, so they're filled as they come near unless told `lazy: false`
            lazy: lazy ?? !!every,
            // Only an owned run gets this far for a single match. Every match's containers belong to the run that paid,
            // and the iframe budget keeps them from piling up.
            beforeRefill: every ? null : () => runner.owner.regate(),
        });
        if (!runner?.owner) {
            panelModule.addPermanentPlacement(placement);
            return;
        }
        if (!previous) {
            panelModule.addPlacement(placement);
            return placement;
        }
        return previous.find(existing => existing.isSameAs(placement)) ?? placement;
    } catch (e) {
        merchantFunctionModule.handleException(e, journey?.alias);
    }
}

function runJourneyAction(runner: JourneyActionRunner, action: JourneyAction, previous?: PanelPlacement[]): PanelPlacement | undefined {
    const {journey} = runner;
    action = evaluateTemplate(action);

    const type = action.type || USER_JOURNEY_ACTION_TYPE.callback;
    switch (type) {
        case USER_JOURNEY_ACTION_TYPE.callback: {
            let placement: PanelPlacement | undefined;
            for (const key of Object.keys(action)) {
                if (key === "type") {
                    continue;
                }
                if (key === USER_JOURNEY_ACTION_TYPE.createPanel && merchantFunctionModule.functions[key] == null) {
                    // The built-in, called here instead so that the run owns what it puts up. A merchant's own
                    // createPanel overrides it, as with any other function.
                    placement = createJourneyPanel(toArray(action[key])[0], journey, runner, previous);
                    continue;
                }
                merchantFunctionModule.call([key, ...toArray(action[key]), journey]);
            }
            return placement;
        }
        // TODO: @Darius Deprecate everything below. Deprecate action type,
        //  everything can be done through built-in functions now, and the journey
        //  syntax for it is much nicer.
        case USER_JOURNEY_ACTION_TYPE.blinkAction: {
            runBlinkAction(action.blinkAction);
            break;
        }
        case USER_JOURNEY_ACTION_TYPE.setVariable: {
            for (const key of Object.keys(action)) {
                if (key !== "type") {
                    merchantVariableModule.set(key, action[key]);
                }
            }
            break;
        }
        case USER_JOURNEY_ACTION_TYPE.editPanel: {
            const options = {
                ...action,
            };
            delete options.type;
            delete options.panel;
            // What is left is panel options. The two keys just dropped are the action's own routing ones,
            // and JourneyAction declares everything else as unknown
            editPanel(action.panel, options as PanelPromptOptions);
            break;
        }
        case USER_JOURNEY_ACTION_TYPE.createPanel: {
            // Unlike the built-in's, this type is the action's own, not the panel's
            const {type, ...panelAction} = action;
            return createJourneyPanel(panelAction, journey, runner, previous);
        }
        default: break;
    }
}

// TODO @cleanup all of these should probably by a call to a SDK method
const blinkActionMap: Record<string, (...args: string[]) => void> = {
    login: (panel: string) => uxFlowModule.enterFlow(PANEL_TYPE.auth, {panel}), // TODO @cleanup blinkSDK.promptAuth(...args)
    donate: (panel: string) => uxFlowModule.enterFlow(PANEL_TYPE.donation, {
        panel,
        skipCTA: userDataModule.isAuthenticated(),
    }),
    giftSubscription: (panel: string) => uxFlowModule.enterFlow(PANEL_TYPE.giftSubscription, {panel}),
    subscribe: (panel: string) => uxFlowModule.enterFlow(PANEL_TYPE.subscribe, {panel}),
    newsletter: (panel: string) => panelModule.createPopup({
        panel,
        type: PANEL_TYPE.newsletter,
        skipCTA: true,
    }),
    setToken: (token: string) => {
        try {
            const decodedToken = base64Decode(token);
            // TODO @Mihai standardize how our tokens are formatted
            // TODO @Mihai this should be a one-time token, to not actually give away the session
            userDataModule.setMerchantToken({
                key: decodedToken.t,
                expiresAt: decodedToken.e,
            });
        } catch (e) {
            console.error("Failed to set the token", e);
        }

    },
    markVisit: (href: string) => markURLVisited(href),
    dashboard: (panel: string) => panelModule.createPopup({panel, type: PANEL_TYPE.dashboard}),
};

// An example blink action string is "socialAuth!facebook-subscribe!yearly!FB10",
// which should be translated into the following sequence of actions:
//  socialAuth("facebook");
//  subscribe("yearly", "FB10");
export function runBlinkAction(actions: BlinkAction | BlinkAction[] | null): void {
    if (isString(actions)) {
        actions = actions.split(",");
    }
    for (const action of toArray<BlinkAction>(actions)) {
        if (!isString(action)) {
            const runner = new JourneyActionRunner({id: "inline", options: {action}, alias: "inline_journey"});
            runner.runAction();
            continue;
        }

        // For each individual action, split by "!" to separate the action and arguments.
        // The action will be the first string after splitting.
        const blinkActionArgs = action.split("!");
        if (blinkActionArgs.length === 0) {
            continue;
        }
        const journey = getMerchantUserJourney(blinkActionArgs[0]);
        if (journey) {
            (new JourneyActionRunner(journey)).runAction();
        } else if (blinkActionMap[blinkActionArgs[0]]) {
            blinkActionMap[blinkActionArgs[0]](...blinkActionArgs.slice(1));
        }
    }
}
